import { EventStatus } from '../constants/eventStatus';
import { Ticket } from '../models/tickets';
import { CheckoutState } from '../state';
import { getScreenWidth, getUserAgent } from '../utils/browser';
import {
    hasRemainingQuantityDisplayEnabled,
    hasSomeTicketWithMoreThanOneVariant,
} from '../utils/tickets';
import {
    getCorrelationId,
    getIsRegEvent,
    getSessionId,
    hasFewTicketsLeftUrgencySignal,
    hasSalesEndSoonUrgencySignal,
    isAndroidApp,
    isEventbriteTLD,
    isIosApp,
} from './app';
import { getIsApplePaySupported } from './applePay';
import {
    getEventId,
    getEventOrganizationId,
    getEventStatus,
    getHasPromoCode,
    getIsEventOnline,
    getIsFreeEvent,
    getIsReservedSeating,
    getSurveyType,
    getTicketReseller,
    getWaitlistSettings,
    hasSpecialInstructions,
    isDonation,
    isListedEvent,
    isLockedEvent,
    isSalesClient,
    isSeries,
    isSeriesParent,
} from './event';
import {
    hasPrepaymentQuestions,
    hasQuestions,
    isApplePayAvailableAsExpressPaymentMethod,
    isGooglePayAvailableAsExpressPaymentMethod,
    isPaypalAvailableAsExpressPaymentMethod,
} from './expressCheckoutConditions';
import {
    getIsPostCheckoutMandatoryQuestionsSupported,
    hasOfflinePaymentMethods,
} from './order/typedOrderSelectors';
import {
    hasInventoryQuestions,
    hasOptionalQuestions,
    hasRequiredCannedQuestions,
    hasRequiredCustomQuestions,
    hasTaxQuestions,
    hasWaiverQuestions,
} from './survey';
import {
    getHasAddonsTicketsAvailable,
    getHasDonationTicketsAvailable,
} from './tickets';
import { getTicketsById } from './tickets/pureSelectors';
import { getUserId, isAuthenticated, isUserLoggedIn } from './user';

export const getStatsigConfigFromState = (state: CheckoutState) => {
    const userAgent = getUserAgent() ?? {};
    const tickets: Ticket[] = Object.values(getTicketsById(state));
    const isFree = !!getIsFreeEvent(state);
    const isLoggedIn = isAuthenticated(state) || isUserLoggedIn(state);

    return {
        custom: {
            browserAndDeviceSupportApplePay: getIsApplePaySupported(state),
            eventId: getEventId(state),

            ...(userAgent
                ? { userAgentLowerCase: userAgent.toLowerCase() }
                : {}),
            hasAddonsAvailable: getHasAddonsTicketsAvailable(state),
            hasDonationTicketsAvailable: getHasDonationTicketsAvailable(state),
            hasFewTicketsLeftUrgencySignal:
                hasFewTicketsLeftUrgencySignal(state),
            hasInventoryQuestions: hasInventoryQuestions(state),
            hasMoreThanOneTicketType: tickets.length > 1,
            hasMultivariantTickets:
                hasSomeTicketWithMoreThanOneVariant(tickets),
            hasOfflinePaymentMethods: hasOfflinePaymentMethods(state),
            hasOptionalQuestions: hasOptionalQuestions(state),
            hasPrepaymentQuestions: hasPrepaymentQuestions(state),
            hasPromoCode: getHasPromoCode(state),
            hasQuestions: hasQuestions(state),
            hasRemainingQuantityDisplay:
                hasRemainingQuantityDisplayEnabled(tickets),
            hasRequiredCannedQuestions: hasRequiredCannedQuestions(state),
            hasRequiredCustomQuestions: hasRequiredCustomQuestions(state),
            hasSalesEndSoonUrgencySignal: hasSalesEndSoonUrgencySignal(state),
            hasScreenWidth: getScreenWidth(),
            hasSpecialInstructions: hasSpecialInstructions(state),
            hasTaxQuestions: hasTaxQuestions(state),
            hasTicketResellers: !!getTicketReseller(state),
            hasWaiverQuestions: hasWaiverQuestions(state),
            isApplePayEPPAvailable:
                isApplePayAvailableAsExpressPaymentMethod(state),
            isDonation: isDonation(state),
            isEventbriteTLD: isEventbriteTLD(state),
            isFree: isFree,
            isGooglePayEPPAvailable:
                isGooglePayAvailableAsExpressPaymentMethod(state),
            isLive: getEventStatus(state) === EventStatus.live,
            isLocked: isLockedEvent(state),
            isPaid: !isFree,
            isPaypalEPPAvailable:
                isPaypalAvailableAsExpressPaymentMethod(state),
            isPostCheckoutMandatoryQuestionsSupported:
                getIsPostCheckoutMandatoryQuestionsSupported(state),
            isRegistrationEvent: !!getIsRegEvent(state),
            isReservedSeating: getIsReservedSeating(state),
            isSalesClient: isSalesClient(state),
            isSeries: isSeries(state),
            isSeriesParent: isSeriesParent(state),
            isUserLoggedIn: isLoggedIn,
            isWaitlist: !!getWaitlistSettings(state),
            listed: isListedEvent(state),
            onlineEvent: getIsEventOnline(state),
            surveyType: getSurveyType(state),
            useAndroidProfile: isAndroidApp(state),
            useIosProfile: isIosApp(state),
        },
        customIDs: {
            correlationId: getCorrelationId(state),
            eventId: getEventId(state),
            organizerId: getEventOrganizationId(state),
            sessionId: getSessionId(state),
        },
        userID: getUserId(state),
    };
};
